<script setup lang="ts">
import {
  NUTRIENT_DECIMAL_PLACES,
  NUTRIENT_LABELS,
  NUTRIENT_UNITS,
} from '@shared/app/utils/calcRecipeNutrients';
import type {
  MainNutrientKey,
  RecipeNutrients,
} from '@shared/app/utils/calcRecipeNutrients';

// NOTE: 栄養素表示用の定義（テーブルで2列表示）
const NUTRIENT_DISPLAY_ORDER = [
  ['energy', 'vitamin_riboflavin'],
  ['protein', 'vitamin_folate'],
  ['lipid', 'vitamin_ascorbic_acid'],
  ['carbohydrate', 'cholesterol'],
  ['mineral_calcium', 'dietary_fibers_total'],
  ['mineral_iron', 'salt_equiv'],
  ['vitamin_thiamin'],
] as const;

const props = defineProps<{
  nutrients: RecipeNutrients;
}>();

/**
 * 栄養素のラベルを取得する
 */
function getNutrientLabel(key: string): string {
  return NUTRIENT_LABELS[key as MainNutrientKey] ?? key;
}

/**
 * 栄養素の単位を取得する
 */
function getNutrientUnit(key: string): string {
  return NUTRIENT_UNITS[key as MainNutrientKey] ?? '';
}

/**
 * 栄養素の表示値を取得する
 * 日本食品標準成分表の表示規格に準拠し、小数点桁数を固定で表示
 */
function getNutrientDisplayValue(key: string): string {
  const nutrientKey = key as MainNutrientKey;
  const value = props.nutrients[nutrientKey];
  if (value === undefined || value === null) {
    return '-';
  }
  const decimalPlaces = NUTRIENT_DECIMAL_PLACES[nutrientKey] ?? 0;
  return value.toFixed(decimalPlaces);
}
</script>

<template>
  <div class="nutrient-table">
    <h2 class="mb-3">
      <span class="text-brown-300 text-xl font-bold">栄養素</span>
      <span class="text-gray-500">（1人分）</span>
    </h2>

    <table class="w-full text-gray-500 text-sm">
      <tbody>
        <tr
          v-for="(row, rowIndex) in NUTRIENT_DISPLAY_ORDER"
          :key="rowIndex"
          class="border-t border-cream-500"
        >
          <template v-for="key in row" :key="key">
            <th class="bg-cream-200 w-1/4 text-left px-2 py-1.5">
              {{ getNutrientLabel(key) }}
            </th>
            <td class="w-1/4 text-right px-2 py-1.5">
              {{ getNutrientDisplayValue(key) }}{{ getNutrientUnit(key) }}
            </td>
          </template>

          <!-- 1列の場合は空セルを追加 -->
          <template v-if="row.length < 2">
            <td class="w-1/4"></td>
            <td class="w-1/4"></td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>
